import React from 'react';
import { Text } from '@components/Text';
import { RedesignButton } from '@components/RedesignButton';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { useAppDispatch } from 'src/hooks';
import moment from 'moment';
import c from 'clsx';
import S from './HostNotificationsModal.module.scss';

interface HostNotificationsModalProps {
  requests: any[];
}

export const HostNotificationsModal: React.FC<HostNotificationsModalProps> = ({ requests }) => {
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeCurrentModalAction());
  };

  const handleApprove = (requestId: string) => {
    // TODO: Implement approve API call
    console.log('Approve request:', requestId);
  };

  const handleDeny = (requestId: string) => {
    // TODO: Implement deny API call
    console.log('Deny request:', requestId);
  };

  if (!requests || requests.length === 0) {
    return (
      <div className={S.modalContent}>
        <div className={S.emptyState}>
          <Text variation="h4" bold className={S.title}>
            No Pending Requests
          </Text>
          <Text variation="body" color="black-400">
            You have no pending cancellation or transfer requests at this time.
          </Text>
          <RedesignButton
            variation="primary"
            height={52}
            onClick={handleClose}
            className={S.closeButton}
          >
            <Text regular color="white-100">
              Close
            </Text>
          </RedesignButton>
        </div>
      </div>
    );
  }

  return (
    <div className={S.modalContent}>
      <div className={S.requestsList}>
        {requests.map((request) => (
          <div key={request.id} className={S.requestCard}>
            <div className={S.requestHeader}>
              <div className={S.requestInfo}>
                <Text variation="h5" bold>
                  {request.type === 'refund' ? 'Cancellation Request' : 'Transfer Request'}
                </Text>
                <Text variation="body-small" color="black-400">
                  Order #{request.cart.friendly_cart_id} • {request.event.title}
                </Text>
              </div>
              <div className={c(S.statusBadge, S[`status-${request.status}`])}>
                <Text variation="caption" color="white-100">
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </Text>
              </div>
            </div>

            <div className={S.requestDetails}>
              <Text variation="body-small" bold className={S.detailLabel}>
                Attendees:
              </Text>
              <div className={S.attendeesList}>
                {request.attendees.map((attendee, index) => (
                  <Text key={attendee.id} variation="body-small" color="black-400">
                    {attendee.first_name} {attendee.last_name} ({attendee.email})
                    {index < request.attendees.length - 1 && ', '}
                  </Text>
                ))}
              </div>

              <Text variation="body-small" bold className={S.detailLabel}>
                Reason:
              </Text>
              <Text variation="body-small" color="black-400" className={S.reason}>
                {request.reason}
              </Text>

              {request.target_event && (
                <>
                  <Text variation="body-small" bold className={S.detailLabel}>
                    Transfer To:
                  </Text>
                  <Text variation="body-small" color="black-400">
                    {request.target_event.title}
                  </Text>
                </>
              )}

              {request.type === 'refund' && (
                <>
                  <Text variation="body-small" bold className={S.detailLabel}>
                    Refund Amount:
                  </Text>
                  <Text variation="body-small" color="black-400">
                    ${request.refund_amount?.toFixed(2) || '0.00'}
                  </Text>
                </>
              )}

              <Text variation="caption" color="black-400" className={S.timestamp}>
                Requested: {moment(request.created_at).format('MMM DD, YYYY h:mm A')}
              </Text>
            </div>

            <div className={S.requestActions}>
              <RedesignButton
                variation="secondary"
                height={44}
                onClick={() => handleDeny(request.id)}
                className={S.denyButton}
              >
                <Text regular color="error-400">
                  Deny
                </Text>
              </RedesignButton>

              <RedesignButton
                variation="primary"
                height={44}
                onClick={() => handleApprove(request.id)}
                className={S.approveButton}
              >
                <Text regular color="white-100">
                  Approve
                </Text>
              </RedesignButton>
            </div>
          </div>
        ))}
      </div>

      <div className={S.modalFooter}>
        <RedesignButton
          variation="secondary"
          height={52}
          onClick={handleClose}
          className={S.closeButton}
        >
          <Text regular color="brand-400">
            Close
          </Text>
        </RedesignButton>
      </div>
    </div>
  );
};
